import React, { useContext, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { AgreementField } from 'models/Agreement';
import {
  AgreementPageSize,
  AgreementPoint,
  getMovedBox,
  getPointInPoints,
  getResizedBox,
  pointsToPixels,
} from 'agreementCoordinatesUtils';
import FieldEditorContext from '../../providers/FieldEditor/FieldEditor.context';
import { getAgreementFieldGroup, getAgreementFieldText } from 'agreementUtils';
import useMatchMedia from 'hooks/useMatchMedia';

import './FieldBox.styles.scss';

type DragMode = 'move' | 'resize';

type DragState = {
  mode: DragMode;
  startPoint: AgreementPoint;
  startField: AgreementField;
  pageRect: DOMRect;
};

type FieldBoxProps = {
  className?: string;
  field: AgreementField;
  scale: number;
  pageSize: AgreementPageSize;
};

const FieldBox: React.FC<FieldBoxProps> = (props) => {
  const { className, field, scale, pageSize } = props;

  const {
    selectedField,
    placingConfig,
    invalidFieldIds,
    setSelectedFieldId,
    setIsDraggingField,
    updateField,
  } = useContext(FieldEditorContext);

  const dragState = useRef<DragState>();

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const isSelected = selectedField?.id === field.id;

  const { x, y, width, height } = pointsToPixels(field, scale);

  const classes = classNames(
    'field-box',
    `field-box--${field.type}`,
    `field-box--${getAgreementFieldGroup(field)}`,
    {
      'field-box--selected': isSelected,
      'field-box--invalid': invalidFieldIds.includes(field.id),
    },
    className,
  );

  const startDrag =
    (mode: DragMode) => (event: React.PointerEvent<HTMLElement>) => {
      if (placingConfig) return;

      event.stopPropagation();

      const pageRect = event.currentTarget
        .closest('.page-fields-layer')
        .getBoundingClientRect();

      dragState.current = {
        mode,
        startPoint: getPointInPoints(
          event.clientX,
          event.clientY,
          pageRect,
          scale,
        ),
        startField: field,
        pageRect,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
      setSelectedFieldId(field.id);
    };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragState.current) return;

    const { mode, startPoint, startField, pageRect } = dragState.current;
    const point = getPointInPoints(
      event.clientX,
      event.clientY,
      pageRect,
      scale,
    );
    const deltaX = point.x - startPoint.x;
    const deltaY = point.y - startPoint.y;

    setIsDraggingField(true);
    updateField(
      mode === 'move'
        ? getMovedBox(startField, deltaX, deltaY, pageSize)
        : getResizedBox(startField, deltaX, deltaY, pageSize),
    );
  };

  const endDrag = () => {
    dragState.current = undefined;
    setIsDraggingField(false);
  };

  return (
    <div
      className={classes}
      style={{ left: x, top: y, width, height }}
      onPointerDown={startDrag('move')}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => {
        if (!placingConfig) e.stopPropagation();
      }}
    >
      <span className="field-box__text">{getAgreementFieldText(field, t)}</span>

      {isSelected && !isPhablet && (
        <span
          className="field-box__resize-handle"
          onPointerDown={startDrag('resize')}
        />
      )}
    </div>
  );
};

export default FieldBox;
